import { useMutation, useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { api, type Finding, type RequestDetail, type SessionInsights, type SessionSummary } from "./api";
import { ErrorMessage, Loading } from "./components";
import { count, money, pct, timestamp, tokens } from "./format";
import { useForgeEvent } from "./hooks";
import { keyName } from "./logKeys";
import { type PanelTab, RequestPanel, useCanSeeContent } from "./RequestDetail";
import { SessionTimeline } from "./SessionTimeline";
import { duration, inputTokens, summarise } from "./timeline";

const ms = (n: number | null) => (n === null ? "–" : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`);
const clock = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });

/** J / K step through the requests, as in LiteLLM's own log viewer; ignored while typing. */
function useStepKeys(onStep: (delta: number) => void) {
  const latest = useRef(onStep);
  latest.current = onStep;
  useEffect(() => {
    const fn = (e: globalThis.KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.closest("forge-text-field, forge-select"))) return;
      if (e.key === "j" || e.key === "J") { e.preventDefault(); latest.current(1); }
      if (e.key === "k" || e.key === "K") { e.preventDefault(); latest.current(-1); }
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, []);
}

/** One session: where the tokens, cache use, cost and waiting went, and what could have been cheaper. */
export function SessionView({ session, canReview }: { session: SessionSummary; canReview: boolean }) {
  const q = useQuery({
    queryKey: ["session", session.sessionId, session.keyId],
    queryFn: () => api.sessionRequests(session.sessionId!, session.keyId),
  });
  const insights = useQuery({
    queryKey: ["session-insights", session.sessionId, session.keyId],
    queryFn: () => api.sessionInsights(session.sessionId!, session.keyId),
  });
  const [selected, setSelected] = useState(0);
  const [view, setView] = useState<"chart" | "table">("chart");
  const [focus, setFocus] = useState<Finding | null>(null);
  // Kept across requests: stepping with J/K shows the next request on the same tab.
  const [tab, setTab] = useState<PanelTab>("details");
  const rows = q.data ?? [];
  const step = (d: number) => setSelected((i) => Math.min(Math.max(0, i + d), Math.max(0, rows.length - 1)));
  useStepKeys(step);
  const canSeeCurrent = useCanSeeContent(rows[Math.min(selected, Math.max(0, rows.length - 1))]);
  const wide = tab === "sent" && canSeeCurrent;

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  const s = summarise(rows);
  const current = rows[Math.min(selected, rows.length - 1)];
  const highlight = new Set(focus?.requestIds ?? []);
  const nav = (
    <div className="row gap-small session-view__toolbar">
      <span className="spacer" />
      <span className="muted forge-typography--body2">Request {selected + 1} of {rows.length}</span>
      <forge-icon-button aria-label="Previous request (K)" onClick={() => step(-1)} disabled={selected === 0 || undefined}>
        <forge-icon name="keyboard_arrow_up" />
      </forge-icon-button>
      <forge-icon-button aria-label="Next request (J)" onClick={() => step(1)} disabled={selected >= rows.length - 1 || undefined}>
        <forge-icon name="keyboard_arrow_down" />
      </forge-icon-button>
    </div>
  );

  return (
    <div className={`session-view${wide ? " session-view--wide" : ""}`}>
      <div className="session-view__main stack gap-medium">
        <div className="stack gap-xsmall">
          <h2 className="forge-typography--heading4 card-title">{session.tool ?? "Session"} · {count(rows.length)} requests</h2>
          <span className="muted">
            {rows[0] ? `${timestamp(rows[0].startTime)} → ` : ""}{timestamp(session.lastActivity)} · key {keyName(session.keyAlias, session.keyId)}
          </span>
        </div>

        <div className="stat-row">
          <Stat label="Cost (est.)" value={money(s.cost)} />
          <Stat label="Input from cache" value={s.cacheShare === null ? "–" : pct(s.cacheShare)} />
          <Stat label="Largest context" value={`${tokens(s.largestContext)} tokens`} />
          <Stat label="Length" value={duration(s.spanMs)} />
          {s.failed > 0 && <Stat label="Failed" value={count(s.failed)} tone="error" />}
        </div>

        <div className="row gap-small session-view__toolbar">
          <ViewToggle value={view} onChange={setView} />
          <span className="spacer" />
          <span className="muted forge-typography--body2" aria-live="polite">Request {selected + 1} of {rows.length}</span>
          <forge-icon-button aria-label="Previous request (K)" onClick={() => step(-1)} disabled={selected === 0 || undefined}>
            <forge-icon name="keyboard_arrow_up" />
          </forge-icon-button>
          <forge-icon-button aria-label="Next request (J)" onClick={() => step(1)} disabled={selected >= rows.length - 1 || undefined}>
            <forge-icon name="keyboard_arrow_down" />
          </forge-icon-button>
        </div>

        {view === "chart"
          ? <SessionTimeline rows={rows} selected={selected} onSelect={setSelected} highlight={highlight} />
          : <RequestTable rows={rows} selected={selected} onSelect={setSelected} />}
        <p className="muted forge-typography--body2 kbd-hint">Click a bar, or press <kbd>J</kbd> and <kbd>K</kbd>, to step through requests.</p>

        {insights.isPending ? <Loading /> : insights.error ? <ErrorMessage error={insights.error} /> : (
          <InsightsPanel data={insights.data} focus={focus} onFocus={(f) => {
            setFocus(f && focus?.id === f.id ? null : f);
            const first = f ? rows.findIndex((r) => f.requestIds.includes(r.requestId)) : -1;
            if (first >= 0) setSelected(first);
          }} />
        )}
        {canReview && session.sessionId && <ReviewPanel sessionId={session.sessionId} keyId={session.keyId} />}
      </div>

      <aside className="session-view__detail" aria-label="Selected request">
        {current && <RequestPanel d={current} tab={tab} onTab={setTab} nav={nav} />}
      </aside>
    </div>
  );
}

/**
 * Its own component so the listener attaches when the element exists: useForgeEvent binds on mount,
 * and SessionView's first render is the loading state, which has no toggle.
 */
function ViewToggle({ value, onChange }: { value: "chart" | "table"; onChange: (v: "chart" | "table") => void }) {
  const ref = useRef<HTMLElement>(null);
  useForgeEvent(ref, "forge-button-toggle-group-change", (e: CustomEvent) => onChange(e.detail === "table" ? "table" : "chart"));
  return (
    <forge-button-toggle-group ref={ref} value={value} mandatory dense aria-label="Show as">
      <forge-button-toggle value="chart"><forge-icon slot="start" name="show_chart" />Timeline</forge-button-toggle>
      <forge-button-toggle value="table"><forge-icon slot="start" name="table_chart" />Table</forge-button-toggle>
    </forge-button-toggle-group>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "error" }) {
  return (
    <div className={`stat${tone ? ` stat--${tone}` : ""}`}>
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
    </div>
  );
}

function RequestTable({ rows, selected, onSelect }: { rows: RequestDetail[]; selected: number; onSelect: (i: number) => void }) {
  return (
    <div className="table-wrap session-view__table">
      <table className="forge-data-table">
        <thead>
          <tr><th>#</th><th>Time</th><th>Model</th><th className="num">Cost</th><th className="num">Cached</th><th className="num">Written</th>
            <th className="num">New</th><th className="num">Out</th><th className="num">Took</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.requestId} className={`clickable${i === selected ? " is-selected" : ""}`} tabIndex={0} aria-selected={i === selected}
              onClick={() => onSelect(i)}
              onKeyDown={(e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(i); } }}>
              <td>{i + 1}</td>
              <td>{clock(r.startTime)}</td>
              <td>{r.status !== "success" && <span className="status-dot status-dot--fail" />}{r.model}</td>
              <td className="num">{r.cacheHit ? "$0" : money(r.cost.total)}</td>
              <td className="num">{tokens(r.tokens.cacheRead)}</td>
              <td className="num">{tokens(r.tokens.cacheWrite)}</td>
              <td className="num">{tokens(r.tokens.text)}</td>
              <td className="num">{tokens(r.tokens.output)}</td>
              <td className="num">{ms(r.durationMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="visually-hidden">Input total per request: {rows.map((r) => tokens(inputTokens(r))).join(", ")}</span>
    </div>
  );
}

const KIND_ICON = { saving: "piggy_bank", tip: "lightbulb", warning: "warning" } as const;

function InsightsPanel({ data, focus, onFocus }: { data: SessionInsights; focus: Finding | null; onFocus: (f: Finding | null) => void }) {
  return (
    <section className="stack gap-small insights" aria-labelledby="insights-title">
      <h3 id="insights-title" className="forge-typography--heading2 card-title">How this session could cost less</h3>
      {data.findings.length === 0 ? (
        <p className="forge-typography--body2 muted">
          {data.unpriced.length
            ? `No comparison: ${data.unpriced.join(", ")} has no price in the gateway.`
            : "Nothing to suggest: this session used the cache well and a model suited to its size."}
        </p>
      ) : (
        <ul className="insights__list">
          {data.findings.map((f) => (
            <li key={f.id} className={`insight insight--${f.kind}${focus?.id === f.id ? " is-focused" : ""}`}>
              <forge-icon name={KIND_ICON[f.kind]} className="insight__icon" />
              <div className="stack gap-xxsmall">
                <b className="forge-typography--body2">{f.title}</b>
                <span className="forge-typography--body2 muted">{f.detail}</span>
                {f.requestIds.length > 0 && (
                  <button type="button" className="link-button" onClick={() => onFocus(f)} aria-pressed={focus?.id === f.id}>
                    {focus?.id === f.id ? "Show all requests" : `Show ${f.requestIds.length === 1 ? "the request" : `these ${count(f.requestIds.length)} requests`} on the timeline`}
                  </button>
                )}
              </div>
              {f.saving !== null && f.saving > 0 && <span className="insight__saving">save ~{money(f.saving)}</span>}
            </li>
          ))}
        </ul>
      )}
      {data.alternatives.length > 0 && (
        <details className="alternatives">
          <summary className="forge-typography--body2">The premium-model requests on other models</summary>
          <table className="forge-data-table">
            <thead><tr><th>Model</th><th className="num">Estimated cost</th><th /></tr></thead>
            <tbody>
              {data.alternatives.map((a) => (
                <tr key={a.model}>
                  <td>{a.model}</td>
                  <td className="num">{money(a.cost)}</td>
                  <td className="muted">{a.sameFamily ? "" : "different provider: check your tool supports it"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <p className="forge-typography--caption muted">Estimated from list prices and token counts. Only you know whether the work needed a larger model.</p>
    </section>
  );
}

function ReviewPanel({ sessionId, keyId }: { sessionId: string; keyId: string }) {
  const m = useMutation({ mutationFn: () => api.sessionReview(sessionId, keyId) });
  return (
    <section className="stack gap-small review" aria-labelledby="review-title">
      <div className="row gap-small">
        <h3 id="review-title" className="forge-typography--heading2 card-title">AI review</h3>
        <span className="spacer" />
        <forge-button variant="outlined" onClick={() => m.mutate()} disabled={m.isPending || undefined}>
          <forge-icon slot="start" name="auto_awesome" />
          {m.isPending ? "Reviewing…" : m.data ? "Review again" : "Review this session"}
        </forge-button>
      </div>
      {!m.data && !m.error && !m.isPending && (
        <p className="forge-typography--body2 muted">A low-cost model reads this session's numbers (never what was said) and suggests how to work more efficiently.</p>
      )}
      {m.isPending && <forge-skeleton style={{ height: "80px" }} />}
      {m.error && <ErrorMessage error={m.error} />}
      {m.data && (
        <div className="stack gap-small">
          <p className="forge-typography--body2">{m.data.summary}</p>
          <ol className="review__list">
            {m.data.suggestions.map((sg, i) => (
              <li key={i}>
                <b className="forge-typography--body2">{sg.title}</b> <span className={`impact impact--${sg.impact}`}>{sg.impact} impact</span>
                <div className="forge-typography--body2 muted">{sg.detail}</div>
              </li>
            ))}
          </ol>
          <p className="forge-typography--caption muted">
            By {m.data.model}{m.data.cost !== null ? ` · ${money(m.data.cost)}` : ""}{m.data.cached ? " · saved answer, no new model call" : ""} · AI-generated; check it against the numbers above.
          </p>
        </div>
      )}
    </section>
  );
}
